// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import type { SeriesKey } from '../types';
import { rampColour } from '../colours';
import { escapeHtml, formatPercent } from '../utils/format';
import { segHtml, viewHead } from './shared';

const SERIES_OPTS: Array<{ id: SeriesKey; label: string }> = [
  { id: 'NSW-boy', label: 'NSW boys' },
  { id: 'NSW-girl', label: 'NSW girls' },
  { id: 'QLD-boy', label: 'QLD boys' },
  { id: 'QLD-girl', label: 'QLD girls' },
];

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

export function renderLetters(root: HTMLElement, ctx: ViewContext): void {
  const fl = ctx.data.agg.firstLetter;
  let sk: SeriesKey = 'NSW-girl';

  root.innerHTML = `
    ${viewHead(
      'First letters through the decades',
      'What share of top-100 babies had a name starting with each letter, decade by decade. Warmer cells mean a more popular initial — read across a row to watch a letter come into and out of fashion.',
    )}
    <div class="panel">
      <div class="controls">
        <span class="field-label">List:</span>
        ${segHtml('Series', SERIES_OPTS, sk)}
      </div>
      <div class="matrix-scroll" data-role="matrix"></div>
      <p class="zoom-hint">Each cell is the percentage of that decade's top-100 babies (by count) whose name began with that letter. Hover for exact figures.</p>
    </div>
  `;

  const matrixEl = root.querySelector('[data-role="matrix"]') as HTMLElement;

  function paint(): void {
    const byDecade = fl[sk] || {};
    const decades = Object.keys(byDecade)
      .map(Number)
      .sort((a, b) => a - b);
    // max pct for colour normalisation
    let maxPct = 0;
    for (const d of decades) for (const L of LETTERS) maxPct = Math.max(maxPct, byDecade[d]?.[L] || 0);
    const usedLetters = LETTERS.filter((L) => decades.some((d) => (byDecade[d]?.[L] || 0) > 0));

    const head = `<thead><tr><th></th>${decades.map((d) => `<th>${d}s</th>`).join('')}</tr></thead>`;
    const body = usedLetters
      .map((L) => {
        const cells = decades
          .map((d) => {
            const v = byDecade[d]?.[L] || 0;
            const t = maxPct > 0 ? v / maxPct : 0;
            const bg = v > 0 ? rampColour(t) : 'var(--bg-surface)';
            const fg = t > 0.55 ? '#fffdf8' : 'var(--text-secondary)';
            const tip = escapeHtml(`${L} · ${d}s: ${formatPercent(v)} of babies`);
            return `<td style="background:${bg};color:${fg}" data-tip="${tip}">${v >= 0.5 ? v.toFixed(0) : ''}</td>`;
          })
          .join('');
        return `<tr><th>${L}</th>${cells}</tr>`;
      })
      .join('');
    matrixEl.innerHTML = `<table class="matrix">${head}<tbody>${body}</tbody></table>`;
  }

  root.querySelectorAll('[data-opt]').forEach((b) =>
    b.addEventListener('click', () => {
      root.querySelectorAll('[data-opt]').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
      sk = b.getAttribute('data-opt') as SeriesKey;
      paint();
    }),
  );

  paint();
}
